import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { DateRangePicker as ReactDateRangePicker, createStaticRanges } from 'react-date-range';
import { ArrowLeftShort, ArrowRightShort, CalendarDate } from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import { enGB } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './AdvanceDateRangePicker.scss';

const getRangePresets = () => ({
  Today: {
    startDate: moment().startOf('day').toDate(),
    endDate: moment().endOf('day').toDate()
  },
  'This Week': {
    startDate: moment().startOf('isoWeek').toDate(),
    endDate: moment().endOf('isoWeek').toDate()
  },
  'Last Week': {
    startDate: moment().subtract(1, 'week').startOf('isoWeek').toDate(),
    endDate: moment().subtract(1, 'week').endOf('isoWeek').toDate()
  },
  'This Month': {
    startDate: moment().startOf('month').toDate(),
    endDate: moment().endOf('month').toDate()
  },
  'Last Month': {
    startDate: moment().subtract(1, 'month').startOf('month').toDate(),
    endDate: moment().subtract(1, 'month').endOf('month').toDate()
  },
  'This Quarter': {
    startDate: moment().startOf('quarter').toDate(),
    endDate: moment().endOf('quarter').toDate()
  },
  'Last Quarter': {
    startDate: moment().subtract(1, 'quarter').startOf('quarter').toDate(),
    endDate: moment().subtract(1, 'quarter').endOf('quarter').toDate()
  },
  'This Year': {
    startDate: moment().startOf('year').toDate(),
    endDate: moment().endOf('year').toDate()
  },
  'Last 3 Months': {
    startDate: moment().subtract(3, 'month').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate()
  },
  'Last 12 Months': {
    startDate: moment().subtract(12, 'month').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate()
  },
  'Last Year': {
    startDate: moment().subtract(1, 'year').startOf('year').toDate(),
    endDate: moment().subtract(1, 'year').endOf('year').toDate()
  },
  'All Time': {
    startDate: null,
    endDate: null
  }
});

const AdvanceDateRangePicker = ({
  value,
  onChange,
  className = '',
  closeOnSelect = true,
  showNavigator = false,
  handleNavigateDate = () => {},
  rangeType = 'day',
  allowAllTime = true
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);

  const availablePresets = useMemo(() => {
    const presets = getRangePresets();
    if (allowAllTime) return presets;
    const { 'All Time': ignoredAllTime, ...rest } = presets;
    return rest;
  }, [allowAllTime]);

  const staticRanges = useMemo(() => {
    return createStaticRanges(
      Object.keys(availablePresets).map(key => ({
        label: key,
        range: () => ({
          ...availablePresets[key],
          isPresetRange: true,
          rangeType: key
        }),
        isSelected(range) {
          const definedRange = this.range();
          if (!definedRange.startDate || !definedRange.endDate) {
            return !range.startDate && !range.endDate;
          }
          return (
            moment(range.startDate).isSame(definedRange.startDate, 'day') &&
            moment(range.endDate).isSame(definedRange.endDate, 'day')
          );
        }
      }))
    );
  }, [availablePresets]);

  const coerceDate = date => (date ? (date instanceof Date ? date : new Date(date)) : null);
  const pickerRange = {
    ...value,
    startDate: coerceDate(value?.startDate),
    endDate: coerceDate(value?.endDate)
  };

  useEffect(() => {
    if (!showPicker) return undefined;

    const handleClickOutside = event => {
      const clickedInsidePicker = pickerRef.current && pickerRef.current.contains(event.target);
      const clickedInsideTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      if (!clickedInsidePicker && !clickedInsideTrigger) {
        setShowPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPicker]);

  return (
    <div className={`advance-date-range-picker ${className}`.trim()} ref={triggerRef}>
      <div className="d-flex justify-content-between align-items-center" style={{ gap: 4 }}>
        {showNavigator && rangeType !== 'All Time' && (
          <>
            <Button
              size="md"
              variant={'border text-dark'}
              className="px-1 py-1.5 d-flex justify-content-center align-items-center border bg-white"
              style={{ width: '50px', height: '38px' }}
              onClick={() => handleNavigateDate(rangeType, 'Previous')}
            >
              <ArrowLeftShort size={24} />
            </Button>
            <Button
              size="md"
              variant={'border text-dark'}
              className="px-1 py-1.5 d-flex justify-content-center align-items-center border bg-white"
              style={{ width: '50px', height: '38px' }}
              onClick={() => handleNavigateDate(rangeType, 'Next')}
            >
              <ArrowRightShort size={24} />
            </Button>
          </>
        )}
        <div
          className={`border hover d-flex align-items-center bg-white px-2 py-1 ${showPicker ? 'border-dark' : ''}`}
          style={{ borderRadius: 8, width: value.startDate && value.endDate ? 240 : 130, minHeight: '27px' }}
          onClick={() => setShowPicker(true)}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowPicker(true);
            }
          }}
        >
          <CalendarDate className="me-2" size={14} style={{ flexShrink: 0 }} />
          <div style={{ width: 1, height: 18 }} className="bg-dark mx-2" />
          <span className="smallFont py-1" style={{ whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
            {value?.startDate && value?.endDate
              ? moment(value?.startDate).year() === 1970
                ? `Upto ${moment(value?.endDate).format('MMM D, YYYY')}`
                : `${moment(value?.startDate).format('MMM D, YYYY')} - ${moment(value?.endDate).format('MMM D, YYYY')}`
              : allowAllTime
              ? 'All Time'
              : 'Select Range'}
          </span>
        </div>
      </div>

      {showPicker && (
        <div className="advance-date-range-picker__popover" id="custom-date-range-picker" ref={pickerRef}>
          <ReactDateRangePicker
            editableDateInputs={false}
            onChange={range => {
              onChange(range);
              if (range?.selection?.isPresetRange && closeOnSelect) {
                setShowPicker(false);
              }
            }}
            moveRangeOnFirstSelection={false}
            ranges={[pickerRange]}
            staticRanges={staticRanges}
            inputRanges={[]}
            showSelectionPreview
            direction="vertical"
            months={1}
            locale={enGB}
          />
        </div>
      )}
    </div>
  );
};

export default AdvanceDateRangePicker;
